import { FC, useState } from 'react';
import { Button, Card, Divider, RadixTable } from '@features/ui';
import {
  periodOptions,
  groupByOptions,
  productTypeOptions,
} from '@features/admin/reports/reportData';
import { toast } from 'react-toastify';
import {
  ReportPeriod,
  ReportGroupBy,
  TReportRollingConversionData,
  TReportRollingConversionParams,
} from '@src/types/reports';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useReportFilters } from '@hooks/useReportFilters';
import { useReportTableColumns } from '@hooks/useReportTableColumns';
import { useReportChartData, getChartColor } from '@hooks/useReportChartData';
import dayjs from 'dayjs';
import { downloadFile } from '@lib/utils';

import { ReportFilteringSettings } from '../ReportFilteringSettings';
import { exportRollingConversionReport, getRollingConversionReport } from '@src/services';

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    dataKey: string;
    color: string;
    payload: any;
    name: string;
  }>;
}

const CustomTooltip: FC<TooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="bg-card-stat p-2.5 rounded-lg text-xs border border-[#3D3D3D] flex flex-col gap-y-1 min-w-[200px]"
        style={{ boxShadow: '0px 7px 13px 0px #B6D72C14' }}
      >
        <p className="text-[#999999] text-[10px] text-center mb-1">
          {payload[0]?.payload?.date}
        </p>
        {payload.map((entry, index) => (
          <div key={index} className="flex justify-between items-center gap-4">
            <span className="text-gray-300 text-xs">{entry.name}:</span>
            <span
              className="text-white font-semibold"
              style={{ color: entry.color }}
            >
              {entry.dataKey.includes('conversion_percentage')
                ? `${entry.value.toFixed(1)}%`
                : entry.dataKey.includes('revenue')
                  ? `$${entry.value.toLocaleString()}`
                  : entry.value.toLocaleString()}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return null;
};

export const RollingConversion: FC = () => {
  // Data state
  const [data, setData] = useState<TReportRollingConversionData[]>([]);

  // Rolling days state
  const [rollingDays, setRollingDays] = useState<string>('30');

  // Use custom hooks for filter management
  const filters = useReportFilters({
    defaultPeriod: ReportPeriod.LAST_7_DAYS,
    defaultGroupBy: ReportGroupBy.DAY,
    enableComparisons: true,
  });

  const { columns, displayItems, tableData } = useReportTableColumns({
    data,
    selectedItems: ['conversion_percentage'],
    compareToPreviousPeriod: filters.compareToPreviousPeriod,
    compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
    getAccessField: (item) => item.date,
    getMainDataField: (item, _) => item.conversion_percentage,
    getPreviousPeriodField: (item, _) => item.conversion_percentage_previous,
    getPreviousYearField: (item, _) => item.conversion_percentage_previous_year,
    includeTotalsRow: false,
    valueFormatter: (_, value) => `${value.toFixed(2)}%`,
    columnTitleFormatter: (key) => {
      if (key === 'conversion_percentage') return 'Conversion (%)';
      if (key === 'conversion_percentage-previous')
        return 'Conversion (%) (Previous Period)';
      if (key === 'conversion_percentage-previous-year')
        return 'Conversion (%) (Previous Year)';

      return key;
    },
  });

  // Use custom hook for chart data transformation
  const chartData = useReportChartData({
    data,
    displayItems,
    compareToPreviousPeriod: filters.compareToPreviousPeriod,
    compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
    getAccessField: (item) => item.date,
    getMainDataField: (item, _) => item.conversion_percentage,
    getPreviousPeriodField: (item, _) => item.conversion_percentage_previous,
    getPreviousYearField: (item, _) => item.conversion_percentage_previous_year,
  });

  const buildPayload = (): TReportRollingConversionParams => {
    return {
      start_date: filters.startDate
        ? dayjs(filters.startDate).format('YYYY-MM-DD')
        : dayjs().subtract(7, 'days').format('YYYY-MM-DD'),
      end_date: filters.endDate
        ? dayjs(filters.endDate).format('YYYY-MM-DD')
        : dayjs().format('YYYY-MM-DD'),
      grouping: (filters.selectedGroupBy?.toLowerCase() ?? 'day') as string,
      rolling_days: parseInt(rollingDays, 10),
      product_ids:
        filters.selectedProducts.length > 0
          ? filters.selectedProducts.map((p) => parseInt(p, 10))
          : undefined,
      compare_to_previous_period: filters.compareToPreviousPeriod,
      compare_to_same_period_previous_year:
        filters.compareToSamePeriodPreviousYear,
    };
  };

  const handleRunReport = async () => {
    filters.setIsLoading(true);
    try {
      const payload = buildPayload();
      const response = await getRollingConversionReport(payload);

      setData(response);
      toast.success('Report generated successfully!');
    } catch (error) {
      toast.error('Error fetching report');
      console.error('Error fetching report:', error);
    } finally {
      filters.setIsLoading(false);
    }
  };

  const handleExportReport = async (format: 'csv' | 'xlsx') => {
    try {
      const payload = buildPayload();
      const response = await exportRollingConversionReport(payload, format);

      if (format === 'csv') {
        const csvBlob = new Blob([response.data], { type: 'text/csv' });
        const csvUrl = URL.createObjectURL(csvBlob);
        const link = document.createElement('a');

        link.href = csvUrl;
        link.download = 'rolling_conversion_report.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(csvUrl);
      } else {
        downloadFile(response.data, 'rolling-conversion-report');
      }

      toast.success('Report exported successfully!');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report');
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">
        Rolling Conversion - Percentage of users who convert from free to paid
        within given amount of days
      </h1>

      <Card className="p-6">
        <ReportFilteringSettings
          compareToPreviousPeriod={filters.compareToPreviousPeriod}
          compareToSamePeriodPreviousYear={
            filters.compareToSamePeriodPreviousYear
          }
          dateRangeValue={filters.valueStart}
          groupByOptions={groupByOptions}
          isLoading={filters.isLoading}
          periodOptions={periodOptions}
          periodRange={filters.periodRange}
          productOptions={productTypeOptions}
          selectedGroupBy={filters.selectedGroupBy}
          selectedPeriod={filters.selectedPeriod}
          selectedRollingDays={rollingDays}
          showCompareToPreviousPeriod={filters.showPreviousPeriodCheckbox}
          showCompareToSamePeriodPreviousYear={filters.showPreviousYearCheckbox}
          showProductsFilter={true}
          showRollingDaysFilter={true}
          onCompareToPreviousPeriodChange={filters.setCompareToPreviousPeriod}
          onCompareToSamePeriodPreviousYearChange={
            filters.setCompareToSamePeriodPreviousYear
          }
          onDateRangeChange={filters.setValueStart}
          onGroupByChange={filters.setSelectedGroupBy}
          onPeriodChange={filters.setSelectedPeriod}
          onProductsChange={filters.setSelectedProducts}
          onRollingDaysChange={setRollingDays}
          onRunReport={handleRunReport}
        />
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <ResponsiveContainer height={400} width="100%">
            <LineChart data={chartData}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                stroke="#999"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#999"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                iconType="line"
                wrapperStyle={{ fontSize: '12px', paddingTop: '20px' }}
              />

              {/* Main data line */}
              <Line
                key="conversion_percentage"
                activeDot={{ r: 6 }}
                dataKey="conversion_percentage"
                dot={{ fill: getChartColor(0), r: 4 }}
                name="Conversion"
                stroke={getChartColor(0)}
                strokeWidth={2}
                type="monotone"
              />

              {/* Previous period line */}
              {filters.compareToPreviousPeriod && (
                <Line
                  key="conversion_percentage_previous_period"
                  activeDot={{ r: 4 }}
                  dataKey="conversion_percentage_previous_period"
                  dot={{
                    fill: getChartColor(0),
                    r: 3,
                  }}
                  name="Conversion (Previous Period)"
                  stroke={getChartColor(0)}
                  strokeDasharray="5 5"
                  strokeWidth={1.5}
                  type="monotone"
                />
              )}

              {/* Previous year line */}
              {filters.compareToSamePeriodPreviousYear && (
                <Line
                  key="conversion_percentage_previous_year"
                  activeDot={{ r: 4 }}
                  dataKey="conversion_percentage_previous_year"
                  dot={{
                    fill: getChartColor(0),
                    r: 3,
                  }}
                  name="Conversion (Previous Year)"
                  stroke={getChartColor(0)}
                  strokeDasharray="2 2"
                  strokeWidth={1.5}
                  type="monotone"
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white">Detailed Data</h2>
          <RadixTable columns={columns} data={tableData} />
          <Divider className="my-4" />
          <div className="flex justify-end gap-2">
            <Button
              className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
              variant="outline"
              onClick={() => handleExportReport('csv')}
            >
              Download CSV
            </Button>
            <Button
              className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
              variant="outline"
              onClick={() => handleExportReport('xlsx')}
            >
              Download Excel
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
